import { useEffect, useRef } from 'react';

import { useLocationChange } from '@/hooks/useLocationChange';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';

export type PlanPageType = 'edit' | 'assign';

interface PlanPageSession {
  active: boolean;
  pageType: PlanPageType | null;
  timerStartedAt: number | null;
}

const DEFAULT_SESSION: PlanPageSession = {
  active: false,
  pageType: null,
  timerStartedAt: null,
};

const MANAGE_PLANS_PREFIX = '/plans/manage-plans/';

const classifyPlanPage = (pathname: string): PlanPageType | null => {
  if (!pathname.startsWith(MANAGE_PLANS_PREFIX)) return null;

  const rest = pathname.slice(MANAGE_PLANS_PREFIX.length);
  const segments = rest.split('/').filter(Boolean);

  // /plans/manage-plans/:planId/edit
  if (segments.length === 2 && segments[1] === 'edit') return 'edit';
  // /plans/manage-plans/:planId (exact — no further segments)
  if (segments.length === 1) return 'assign';

  return null;
};

const EVENT_NAMES: Record<PlanPageType, string> = {
  edit: 'plan_edit_time_spent',
  assign: 'plan_assign_time_spent',
};

const computeDurationSeconds = (session: PlanPageSession): number => {
  if (session.timerStartedAt === null) return 0;
  return Math.round((Date.now() - session.timerStartedAt) / 1000);
};

export const usePlanPageTimeTracker = () => {
  const track = useAmplitudeTrack();

  const trackRef = useRef(track);
  useEffect(() => {
    trackRef.current = track;
  }, [track]);

  const sessionRef = useRef({ ...DEFAULT_SESSION });

  const fireEvent = () => {
    const trackFn = trackRef.current;
    const session = sessionRef.current;
    if (!trackFn || !session.active || !session.pageType) return;

    trackFn(EVENT_NAMES[session.pageType], {
      duration_seconds: computeDurationSeconds(session),
    });
  };

  useLocationChange((curr, prev) => {
    if (!curr?.pathname) return;

    const currPageType = classifyPlanPage(curr.pathname);
    const prevPageType = prev?.pathname
      ? classifyPlanPage(prev.pathname)
      : null;

    // Leaving a tracked page
    if (prevPageType && prevPageType !== currPageType) {
      fireEvent();
      sessionRef.current = { ...DEFAULT_SESSION };
    }

    // Entering a tracked page
    if (currPageType && currPageType !== prevPageType) {
      sessionRef.current = {
        active: true,
        pageType: currPageType,
        timerStartedAt: Date.now(),
      };
    }
  });

  useEffect(() => {
    const handleBeforeUnload = () => {
      fireEvent();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  });
};
